// Settings store — theme / density / contrast / font-scale, persisted to localStorage.
// Applied to document.documentElement.dataset before first paint via main.tsx.

import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { Density, FontScale, Theme } from "@/lib/types";

interface SettingsState {
  theme: Theme;
  density: Density;
  fontScale: FontScale;
  highContrast: boolean;
  reduceMotion: boolean;
  setTheme: (t: Theme) => void;
  setDensity: (d: Density) => void;
  setFontScale: (s: FontScale) => void;
  setHighContrast: (v: boolean) => void;
  setReduceMotion: (v: boolean) => void;
  applyToDocument: () => void;
}

export const useSettingsStore = create<SettingsState>()(
  persist(
    (set, get) => ({
      theme: "system",
      density: "comfortable",
      fontScale: "md",
      highContrast: false,
      reduceMotion: false,
      setTheme: (theme) => { set({ theme }); get().applyToDocument(); },
      setDensity: (density) => { set({ density }); get().applyToDocument(); },
      setFontScale: (fontScale) => { set({ fontScale }); get().applyToDocument(); },
      setHighContrast: (highContrast) => { set({ highContrast }); get().applyToDocument(); },
      setReduceMotion: (reduceMotion) => { set({ reduceMotion }); get().applyToDocument(); },
      applyToDocument: () => {
        const { theme, density, fontScale, highContrast, reduceMotion } = get();
        const root = document.documentElement;
        const effectiveTheme = theme === "system"
          ? (matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light")
          : theme;
        root.dataset.theme = effectiveTheme;
        root.dataset.density = density;
        root.dataset.fontScale = fontScale;
        root.dataset.contrast = highContrast ? "high" : "normal";
        if (reduceMotion) root.style.setProperty("--motion-override", "0ms");
        else root.style.removeProperty("--motion-override");
      },
    }),
    { name: "lexicon.settings" },
  ),
);
